import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../commonDesign/Button'
import { provisionedTargets } from '../../../../../../api/integrations'
import { decipherError } from '../../../../../../utils/decipherError'

const PARTITION_ACTIONS = [
  {
    value: 'retain',
    label: 'Retain',
    description: 'Keep the partition and all its data intact.'
  },
  {
    value: 'truncate',
    label: 'Truncate',
    description: 'Keep the partition structure but delete all rows from it.'
  },
  {
    value: 'drop',
    label: 'Drop',
    description: 'Remove the partition entirely, including all data.'
  }
]

function UnassignJobForm ({ job, onCancel }) {
  const [partitionAction, setPartitionAction] = useState('retain')
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: unassignJob } = provisionedTargets.useUnassignJobFromProvisionedTarget()

  const handleUnassign = useCallback(async () => {
    if (!job) return
    try {
      setError(null)
      setProcessing(true)
      await unassignJob({
        jobConfigurationId: job.jobConfigurationId,
        partitionAction
      })
      onCancel?.()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [job, partitionAction, unassignJob, onCancel])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <p style={{ margin: '0 0 0.5rem' }}>
          You are about to unassign <strong>{job?.jobKey}</strong> from its provisioned target.
        </p>
        <p style={{ margin: '0 0 1rem', opacity: 0.7, fontSize: '0.875rem' }}>
          If this is the last job for its connection on the target, you can choose what happens to the partition data.
        </p>
      </Grid>
      <Grid item xs={12}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {PARTITION_ACTIONS.map(action => (
            <label
              key={action.value}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                padding: '0.75rem',
                border: '1px solid var(--color-border, #ddd)',
                borderRadius: '4px',
                cursor: 'pointer',
                background: partitionAction === action.value
                  ? 'var(--color-primary-light, rgba(0,102,255,0.08))'
                  : undefined
              }}
            >
              <input
                type='radio'
                checked={partitionAction === action.value}
                onChange={() => setPartitionAction(action.value)}
                style={{ marginTop: '2px' }}
              />
              <div>
                <div style={{ fontWeight: 600 }}>{action.label}</div>
                <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>{action.description}</div>
              </div>
            </label>
          ))}
        </div>
      </Grid>
      <Grid item xs={12}>
        <SydModalActions>
          {error ? <div className='__error'>{error}</div> : null}
          <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>
            Cancel
          </SydButton>
          <SydButton
            processing={processing}
            variant='primary'
            priority='warning'
            size='md'
            onClick={handleUnassign}
          >
            Unassign
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

UnassignJobForm.propTypes = {
  job: PropTypes.object,
  onCancel: PropTypes.func
}

export default UnassignJobForm
